import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../../interfaces/user.interface';
import { CacheService } from '../cache.service';
import { CookiesService } from '../cookie.service';

/**
 * The auth service handles the authentication of users in the app.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  /**
   * Array with all the descriptions to unsub from whe the page leaves
   *
   * @private
   * @type {Subscription[]}
   * @memberof ChildDetailPage
   */
  private subscriptions: Subscription[] = [];

  /**
   * Saves the check if user is logged in as Subject
   *
   * @private
   * @memberof AuthService
   */
  private loggedInSubject = new BehaviorSubject<boolean>(false);

  /**
   * Creates an instance of AuthService.
   * @param {HttpClient} http
   * @memberof AuthService
   */
  constructor(
    private http: HttpClient,
    private router: Router,
    private cache: CacheService
  ) {
    this.startLoginChecker();
    this.loggedInSubject.next(this.tokenIsExpired());
  }

  /**
   * Return logged in Subject
   *
   * @returns {Observable<boolean>}
   * @memberof AuthService
   */
  getLoggedIn$(): Observable<boolean> {
    return this.loggedInSubject.asObservable();
  }

  /**
   * Login user and sets logged in subject
   *
   * @param {string} username
   * @param {string} password
   * @returns
   * @memberof AuthService
   */
  login(username: string, password: string) {
    // TODO: check for server response, only return if status is ok
    return this.http
      .post<User>(
        `${environment.apiUrl}loginLocal`,
        {
          username,
          password,
        },
        { withCredentials: true }
      )
      .pipe(tap((res) => this.setSession(res)));
  }

  public setSession(authResult) {
    if (authResult.expires) this.legacySetSession(authResult);
    if (authResult.expiresIn) this.cognitoSetSession(authResult);
    if (authResult.forcePasswordChange)
      this.setForcePasswordChange(authResult.forcePasswordChange);

    this.loggedInSubject.next(true);
  }

  private legacySetSession(authResult) {
    const expiresAt = moment().add(authResult.expires, 'second');
    this.cache.set('id_token', authResult.token, 'local', {
      expires: expiresAt.valueOf(),
    });
  }

  private cognitoSetSession(authResult) {
    const expiresAt = moment().add(authResult.expiresIn, 'second');
    this.cache.set('id_token', 'COGNITO', 'local', {
      expires: expiresAt.valueOf(),
    });
  }

  private setForcePasswordChange(forcePasswordChange: boolean) {
    this.cache.set('forcePasswordChange', forcePasswordChange, 'local');
  }

  /**
   * Returns true if the current user is authenticated via Cognito (cookie-based),
   * false if legacy (JWT in local storage under com-id_token).
   */
  isCognitoUser(): boolean {
    return this.cache.get<string>('id_token', 'local') === 'COGNITO';
  }

  /**
   * Calls the legacy backend logout endpoint so the server can clear HttpOnly auth cookies.
   * Same origin as login (apiUrl), so the browser sends the cookies and the response can clear them.
   * Legacy backend proxies /cognito/logout to Cognito-API and returns Set-Cookie to clear cookies.
   */
  cognitoLogout(): Observable<void> {
    const url = `${environment.apiUrl}cognito/logout`;
    return this.http
      .post<void>(url, {}, { withCredentials: true })
      .pipe(catchError(() => of(undefined)));
  }

  /**
   * Logout user: for legacy users clears local storage; for Cognito users also calls
   * Cognito-API logout so HttpOnly cookies (Authentication, IdToken, RefreshToken) are cleared.
   * Returns an Observable that completes when logout is done. Callers (e.g. header) must wait
   * for this, then call clearSession() and reload/navigate so the UI does not react to
   * loggedInSubject before the reload.
   */
  logout(): Observable<void> {
    if (this.isCognitoUser()) {
      return this.cognitoLogout();
    }
    this.finishLogout();
    return of(undefined);
  }

  /**
   * Clears local session and notifies that the user is logged out. Call this from the
   * logout() subscribe callback (e.g. right before reload) so the UI does not update
   * until the page is about to reload.
   */
  clearSession(): void {
    this.finishLogout();
  }

  private finishLogout(): void {
    this.clearLocalStorage();
    this.loggedInSubject.next(false);
  }

  /**
   * Removes id_token and from local storage
   *
   * @private
   * @memberof AuthService
   */
  private clearLocalStorage() {
    this.cache.clear();
    CookiesService.deleteCookie('__tawkuuid');
    CookiesService.deleteCookie('mycompassion');
  }

  /**
   * Starts checking if you are logged in every time the route changes
   *
   * @private
   * @memberof AuthService
   */
  private startLoginChecker() {
    const sub$ = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const loggedIn = this.tokenIsExpired();
        this.loggedInSubject.next(loggedIn);
        if (!loggedIn && this.cache.exists('id_token')) {
          this.clearLocalStorage();
        }
      });

    this.subscriptions.push(sub$);
  }

  /**
   * Gets token from local storage and converts it to a Moment object
   *
   * @private
   * @returns {moment.Moment}
   * @memberof AuthService
   */
  private tokenIsExpired(): boolean {
    return this.cache.exists('id_token', 'local');
  }

  /**
   * Unsubscribe from the observables
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
